import React, { useEffect, useRef } from "react";
import type { CatalogDocument } from "../hooks/useDocumentMention";
import "./DocumentMentionDropdown.css";

interface MentionDropdownProps {
	results: CatalogDocument[];
	highlightedIndex: number;
	onSelect: (doc: CatalogDocument) => void;
	visible: boolean;
	loading?: boolean;
	onDismiss?: () => void;
}

const groupLabel = (doc: CatalogDocument): string =>
	doc.ref_type === "template" ? "Templates" : "Deal Room";

const MentionDropdown: React.FC<MentionDropdownProps> = ({
	results,
	highlightedIndex,
	onSelect,
	visible,
	loading = false,
	onDismiss,
}) => {
	const listRef = useRef<HTMLUListElement>(null);
	const containerRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		// Scroll by option element (not child index) so group-header and loading
		// rows don't offset the highlight target.
		const options = listRef.current?.querySelectorAll<HTMLElement>(
			'[role="option"]',
		);
		options?.[highlightedIndex]?.scrollIntoView({ block: "nearest" });
	}, [highlightedIndex, loading]);

	// Click-outside dismissal
	useEffect(() => {
		if (!visible || !onDismiss) return;
		const handleClickOutside = (e: MouseEvent) => {
			if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
				onDismiss();
			}
		};
		document.addEventListener("mousedown", handleClickOutside);
		return () => document.removeEventListener("mousedown", handleClickOutside);
	}, [visible, onDismiss]);

	if (!visible) return null;
	if (results.length === 0 && !loading) return null;

	return (
		<div
			ref={containerRef}
			className="absolute bottom-full left-0 right-0 mb-1"
			style={{ zIndex: 1000 }}
		>
			<ul
				ref={listRef}
				className={`max-h-[240px] overflow-y-auto bg-white border border-gray-200 rounded-lg py-1 [&::-webkit-scrollbar]:hidden [scrollbar-width:none] ${
					loading && results.length > 0 ? "opacity-60" : ""
				}`}
				style={{ boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)" }}
				role="listbox"
				aria-busy={loading}
			>
				{loading && (
					<li
						role="presentation"
						className="px-3 py-2 text-xs text-gray-500 border-b border-gray-100"
					>
						Searching…
					</li>
				)}
				{results.map((doc, idx) => {
					const isTemplate = doc.ref_type === "template";
					// A section header before the first row of each group.
					const showHeader =
						idx === 0 || groupLabel(results[idx - 1]) !== groupLabel(doc);
					return (
						<React.Fragment key={doc.doc_id}>
							{showHeader && (
								<li
									role="presentation"
									className="px-3 pt-2 pb-1 text-[10px] font-semibold text-gray-400 uppercase tracking-wider"
								>
									{groupLabel(doc)}
								</li>
							)}
							<li
								role="option"
								aria-selected={idx === highlightedIndex}
								className={`px-3 py-2 cursor-pointer text-sm transition-colors ${
									idx === highlightedIndex
										? "bg-[#004C4B1A] text-[#004C4B]"
										: "hover:bg-gray-50"
								}`}
								onMouseDown={(e) => {
									e.preventDefault(); // keep textarea focus
									onSelect(doc);
								}}
							>
								<div className="flex items-center gap-2">
									<span className="font-medium truncate">{doc.filename}</span>
									{!isTemplate && doc.document_type && (
										<span className="flex-shrink-0 px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-100 text-gray-600 uppercase tracking-wide">
											{doc.ref_type === "deal" ? "deal room" : doc.document_type}
										</span>
									)}
								</div>
								<div className="text-xs text-gray-500 truncate mt-0.5">
									{isTemplate
										? doc.deal_name
										: doc.ref_type === "deal"
											? "Mention entire deal room"
											: [doc.entity_name, doc.period_end_date, doc.deal_name]
													.filter(Boolean)
													.join(" · ")}
								</div>
							</li>
						</React.Fragment>
					);
				})}
			</ul>
		</div>
	);
};

export default MentionDropdown;
